import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

/**
 * Microphone permission status across web (Permissions API + getUserMedia)
 * and native (capacitor-voice-recorder, which wraps AVAudioSession on iOS
 * and runtime RECORD_AUDIO on Android).
 *
 * - granted: mic is usable now.
 * - denied:  user said no. On iOS this is permanent until they flip it
 *            in Settings (the OS will not show the prompt again for the
 *            life of this install). Android is similar after two denials.
 * - prompt:  permission has not been decided yet — calling
 *            `requestMicrophonePermission()` will surface the OS dialog.
 * - unavailable: platform can't grant a mic at all (no input device,
 *                insecure web context, etc).
 */
export type MicPermissionStatus =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unavailable';

/**
 * Read the current mic permission state without prompting.
 * Useful for "should I show a priming UI?" decisions.
 */
export async function getMicrophonePermissionStatus(): Promise<MicPermissionStatus> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { VoiceRecorder } = await import('capacitor-voice-recorder');
      const can = await VoiceRecorder.canDeviceVoiceRecord();
      if (!can.value) return 'unavailable';
      const has = await VoiceRecorder.hasAudioRecordingPermission();
      return has.value ? 'granted' : 'prompt';
    } catch {
      return 'unavailable';
    }
  }

  // Web: prefer the standard Permissions API where supported (Chromium,
  // recent Safari); fall back to "prompt" so the caller knows it has to
  // call getUserMedia to actually find out.
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return 'unavailable';
  }
  try {
    // Cast: TS doesn't expose 'microphone' as a PermissionName by default
    // in lib.dom yet, but Chromium/Safari/Firefox all accept it.
    const result = await (navigator.permissions as Permissions | undefined)?.query?.({
      name: 'microphone' as PermissionName,
    });
    if (result?.state === 'granted') return 'granted';
    if (result?.state === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'prompt';
  }
}

/**
 * Trigger the OS mic prompt (or the WebView's). Call this from a UI
 * affordance that the user just tapped — calling it from a Web Worker
 * or far away from a user gesture won't surface a dialog on iOS.
 *
 * Returns the post-prompt status. 'denied' here is a real user choice
 * (or a previously-saved one); the caller should fall back to a Settings
 * deep-link prompt rather than re-asking.
 */
export async function requestMicrophonePermission(): Promise<MicPermissionStatus> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { VoiceRecorder } = await import('capacitor-voice-recorder');
      const can = await VoiceRecorder.canDeviceVoiceRecord();
      if (!can.value) return 'unavailable';
      // requestAudioRecordingPermission returns the post-decision state.
      // On iOS this dialog can only appear once per install; subsequent
      // calls return the cached decision.
      const res = await VoiceRecorder.requestAudioRecordingPermission();
      return res.value ? 'granted' : 'denied';
    } catch {
      return 'unavailable';
    }
  }

  // Web: getUserMedia is what actually prompts. Open + immediately stop
  // so we don't leave a track running.
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return 'unavailable';
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return 'granted';
  } catch (err) {
    // NotAllowedError = user denied (or policy denial). Other errors
    // (NotFoundError, OverconstrainedError) mean no usable device.
    const name = (err as DOMException | undefined)?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
    return 'unavailable';
  }
}

/**
 * Open the OS app-settings page so the user can flip a previously-denied
 * permission. iOS uses the `app-settings:` URL scheme, Android uses a
 * `package:` URI that the App plugin's openUrl maps to ACTION_APPLICATION_DETAILS_SETTINGS.
 * No-op on web — browsers don't expose a programmatic settings opener.
 */
export async function openAppPermissionSettings(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const url =
    Capacitor.getPlatform() === 'ios' ? 'app-settings:' : 'package:to.maie.emr';
  try {
    // openUrl's type surface has shifted across Capacitor versions; the
    // call exists at runtime on both iOS and Android.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (CapApp as any).openUrl?.({ url });
  } catch {
    /* graceful fallback — user can open Settings manually */
  }
}
